import { gql } from "@apollo/client";

const LAP_DATA_FIELDS = `
  lapNumber
  lapsRemaining
  energyEntry
  energy
  predictedEnergy
  target
  eLap
  lapTimeEntry
  lapTime
  predictedLapTime
  lapStartTime
  SoC
  predictedSoC
  pitInSoC
  predictedPitInSoC
  tCellMax
  predictedTCellMax
  activationLap
  freezeLap
  attackMode
  attackSegments
  pitScenario
  comment
  greenCode {
    encryptionCode
    decodedChannels {
      name
      value
    }
  }
  orangeCode {
    encryptionCode
    decodedChannels {
      name
      value
    }
  }
`;

const CAR_RACE_DATA_FIELDS = `
  ressPitBoostLap
  lapTable {
    ${LAP_DATA_FIELDS}
  }
  raceConfig {
    currentLap
    lapOffset
    lapTimeRace
    lapTimeAttack
    targetRace
    targetAttack
    attackScenario
    attackActivations {
      startTime
      endTime
      lap
    }
    attacksRemainingLive
    pitScenario
    targetPitSoc
    pitLap
    dValue
    pitWindowOpenEnergy
    pitWindowCloseEnergy
  }
  offsetPreviews {
    offset
    target
    lapTime
  }
  pitPreviews {
    energy
    target
    lapTime
  }
  optimisationTrigger {
    user
    command
    timestamp
    runTime
    iterations
    dValue
    linear
  }
`;

export const QUERY_RACE_DATA = gql`
  query getRaceData {
    getRaceData {
      carColour
      ${CAR_RACE_DATA_FIELDS}
    }
  }
`;

export const SUBSCRIBE_LAP_UPDATE = gql`
  subscription newLapUpdate {
    newLapUpdate {
      carColour
      ${LAP_DATA_FIELDS}
    }
  }
`;

export const SUBSCRIBE_RACE_CONFIG_UPDATE = gql`
  subscription newRaceConfigUpdate {
    newRaceConfigUpdate {
      carColour
      raceConfig {
        currentLap
        lapOffset
        lapTimeRace
        lapTimeAttack
        targetRace
        targetAttack
        attackScenario
        attackActivations {
          startTime
          endTime
          lap
        }
        attacksRemainingLive
        pitScenario
        targetPitSoc
        pitLap
        dValue
        pitWindowOpenEnergy
        pitWindowCloseEnergy
      }
    }
  }
`;

export const SUBSCRIBE_OFFSET_PREVIEWS_UPDATE = gql`
  subscription newOffsetPreviewsUpdate {
    newOffsetPreviewsUpdate {
      carColour
      offsetPreviews {
        offset
        target
        lapTime
      }
    }
  }
`;

export const SUBSCRIBE_PIT_PREVIEWS_UPDATE = gql`
  subscription newPitPreviewsUpdate {
    newPitPreviewsUpdate {
      carColour
      pitPreviews {
        energy
        target
        lapTime
      }
    }
  }
`;

export const SUBSCRIBE_OPTIMISATION_TRIGGER_UPDATE = gql`
  subscription newOptimisationTriggerUpdate {
    newOptimisationTriggerUpdate {
      carColour
      optimisationTrigger {
        user
        command
        timestamp
        runTime
        iterations
        dValue
        linear
      }
    }
  }
`;

export const SUBSCRIBE_LAP_TABLE_UPDATE = gql`
  subscription newLapTableUpdate {
    newLapTableUpdate {
      carColour
      ressPitBoostLap
      lapTable {
        ${LAP_DATA_FIELDS}
      }
    }
  }
`;

export const MUTATE_SET_LAP_ENCRYPTION_CODE = gql`
  mutation setLapEncryptionCode(
    $carColour: String!
    $lapNumber: Int!
    $encryptionCode: String!
    $encryptionColour: EncryptionCodeColour!
    $decodedChannels: [DecodedEncryptionChannelInput]
  ) {
    setLapEncryptionCode(
      carColour: $carColour
      lapNumber: $lapNumber
      encryptionCode: $encryptionCode
      encryptionColour: $encryptionColour
      decodedChannels: $decodedChannels
    )
  }
`;

export const MUTATE_SET_LAP_ENERGY = gql`
  mutation setLapEnergy($carColour: String!, $lapNumber: Int!, $energy: Float) {
    setLapEnergy(carColour: $carColour, lapNumber: $lapNumber, energy: $energy)
  }
`;

export const MUTATE_SET_LAP_ENERGY_ENTRY = gql`
  mutation setLapEnergyEntry(
    $carColour: String!
    $lapNumber: Int!
    $energyEntry: String
  ) {
    setLapEnergyEntry(
      carColour: $carColour
      lapNumber: $lapNumber
      energyEntry: $energyEntry
    )
  }
`;

export const MUTATE_SET_LAP_TIME_ENTRY = gql`
  mutation setLapTimeEntry(
    $carColour: String!
    $lapNumber: Int!
    $lapTimeEntry: String
  ) {
    setLapTimeEntry(
      carColour: $carColour
      lapNumber: $lapNumber
      lapTimeEntry: $lapTimeEntry
    )
  }
`;

export const MUTATE_SET_LAP_TIME = gql`
  mutation setLapTime($carColour: String!, $lapNumber: Int!, $lapTime: Float) {
    setLapTime(carColour: $carColour, lapNumber: $lapNumber, lapTime: $lapTime)
  }
`;

export const MUTATE_SET_LAP_COMMENT = gql`
  mutation setLapComment(
    $carColour: String!
    $lapNumber: Int!
    $comment: String
  ) {
    setLapComment(
      carColour: $carColour
      lapNumber: $lapNumber
      comment: $comment
    )
  }
`;

export const MUTATE_UPDATE_LAP_OFFSET = gql`
  mutation updateLapOffset($carColour: String!, $increase: Boolean!) {
    updateLapOffset(carColour: $carColour, increase: $increase)
  }
`;

export const MUTATE_SET_ATTACK_SCENARIO = gql`
  mutation setAttackScenario($carColour: String!, $attackScenario: Int!) {
    setAttackScenario(carColour: $carColour, attackScenario: $attackScenario)
  }
`;

export const MUTATE_SET_PIT_SCENARIO = gql`
  mutation setPitScenario($carColour: String!, $pitScenario: Int!) {
    setPitScenario(carColour: $carColour, pitScenario: $pitScenario)
  }
`;

export const MUTATE_SET_TARGET_PIT_SOC = gql`
  mutation setTargetPitSoc($carColour: String!, $targetPitSoc: Float!) {
    setTargetPitSoc(carColour: $carColour, targetPitSoc: $targetPitSoc)
  }
`;
